import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { couponDiscount, meetsMinimum } from '../utils/coupon';

const STORAGE_KEY = 'cs_cart_v1';
const COUPON_KEY = 'cs_coupon_v1';

/* A product's identity across the shop, the cart and the database.
   Supabase products carry both a uuid (supabaseId) and a product_code
   (id); local fallback data only has the latter. */
export const cartKey = (product) =>
  String(product?.supabaseId || product?.id || '');

/* Only the fields the cart and the order need. Keeping the stored copy
   small means a stale localStorage entry can't drag a whole outdated
   product object into a new order. */
const slim = (p) => ({
  id: p.id,
  supabaseId: p.supabaseId || null,
  name: p.name,
  planet: p.planet,
  emoji: p.emoji,
  price: Number(p.price) || 0,
  image: p.image || null,
  stock: Number.isFinite(Number(p.stock)) ? Number(p.stock) : null,
  inStock: p.inStock !== false,
  product_code: p.product_code || p.id,
});

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((line) => line?.product && cartKey(line.product))
      .map((line) => ({
        product: slim(line.product),
        quantity: Math.max(1, Number(line.quantity) || 1),
      }));
  } catch {
    // Corrupt or unavailable storage (private mode) — start empty
    // rather than breaking every page that reads the cart.
    return [];
  }
};

/* The coupon the customer applied, as validate_coupon() described it.
   Held alongside the cart so it survives a wander back to the shop —
   the cart page re-checks it against the database before checkout, so
   a stale copy here can only ever cost a re-validation, never a
   discount the shop no longer offers. */
const readCoupon = () => {
  try {
    const raw = localStorage.getItem(COUPON_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.code ? parsed : null;
  } catch {
    return null;
  }
};

const CartContext = createContext({
  items: [],
  count: 0,
  subtotal: 0,
  coupon: null,
  discount: 0,
  total: 0,
  addItem: () => {},
  removeItem: () => {},
  setQuantity: () => {},
  clear: () => {},
  syncWithCatalogue: () => {},
  applyCoupon: () => {},
  removeCoupon: () => {},
});

export function CartProvider({ children }) {
  const [items, setItems] = useState(read);
  const [coupon, setCoupon] = useState(readCoupon);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* storage full or blocked — the cart still works for this tab */
    }
  }, [items]);

  useEffect(() => {
    try {
      if (coupon) localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
      else localStorage.removeItem(COUPON_KEY);
    } catch {
      /* see above */
    }
  }, [coupon]);

  // Keep a cart open in two tabs in step.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setItems(read());
      if (e.key === COUPON_KEY) setCoupon(readCoupon());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addItem = useCallback((product, qty = 1) => {
    const key = cartKey(product);
    if (!key) return;

    setItems((prev) => {
      const next = slim(product);
      const cap = next.stock && next.stock > 0 ? next.stock : 99;
      const existing = prev.find((line) => cartKey(line.product) === key);

      if (!existing) {
        return [...prev, { product: next, quantity: Math.min(qty, cap) }];
      }

      return prev.map((line) =>
        cartKey(line.product) === key
          ? {
              // Refresh the snapshot too — the shop just loaded it.
              product: next,
              quantity: Math.min(line.quantity + qty, cap),
            }
          : line
      );
    });
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((line) => cartKey(line.product) !== key));
  }, []);

  const setQuantity = useCallback((key, qty) => {
    setItems((prev) =>
      prev.flatMap((line) => {
        if (cartKey(line.product) !== key) return [line];
        if (qty < 1) return []; // stepping below one removes the line
        const stock = line.product.stock;
        const cap = stock && stock > 0 ? stock : 99;
        return [{ ...line, quantity: Math.min(qty, cap) }];
      })
    );
  }, []);

  // The order has been placed (or the customer emptied the cart) —
  // the code belonged to that basket, not to the next one.
  const clear = useCallback(() => {
    setItems([]);
    setCoupon(null);
  }, []);

  const applyCoupon = useCallback((next) => {
    setCoupon(next?.code ? next : null);
  }, []);

  const removeCoupon = useCallback(() => setCoupon(null), []);

  /* Prices and stock in localStorage go stale the moment the admin edits
     a product. The cart page reloads the catalogue and calls this so the
     customer always checks out against what's actually on the shelf. */
  const syncWithCatalogue = useCallback((catalogue) => {
    if (!Array.isArray(catalogue) || catalogue.length === 0) return;

    const byKey = new Map(catalogue.map((p) => [cartKey(p), p]));

    setItems((prev) =>
      prev.flatMap((line) => {
        const fresh = byKey.get(cartKey(line.product));
        // Product pulled from the catalogue — drop it from the cart.
        if (!fresh) return [];

        const next = slim(fresh);
        const cap = next.stock && next.stock > 0 ? next.stock : 99;
        return [{ product: next, quantity: Math.min(line.quantity, cap) }];
      })
    );
  }, []);

  const count = useMemo(
    () => items.reduce((sum, line) => sum + line.quantity, 0),
    [items]
  );

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, line) => sum + Number(line.product.price || 0) * line.quantity,
        0
      ),
    [items]
  );

  /* A coupon stays applied while the basket is too small for it — the
     customer is one item away, and silently dropping their code would
     be worse than showing them why it isn't counting yet. It just
     doesn't take anything off until the minimum is met. */
  const discount = useMemo(
    () =>
      coupon && meetsMinimum(coupon, subtotal)
        ? couponDiscount(coupon, subtotal)
        : 0,
    [coupon, subtotal]
  );

  const total = useMemo(
    () => Math.max(0, subtotal - discount),
    [subtotal, discount]
  );

  const value = useMemo(
    () => ({
      items,
      count,
      subtotal,
      coupon,
      discount,
      total,
      addItem,
      removeItem,
      setQuantity,
      clear,
      syncWithCatalogue,
      applyCoupon,
      removeCoupon,
    }),
    [
      items,
      count,
      subtotal,
      coupon,
      discount,
      total,
      addItem,
      removeItem,
      setQuantity,
      clear,
      syncWithCatalogue,
      applyCoupon,
      removeCoupon,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
